<script setup lang="ts">
import { ref, nextTick, onMounted, onUnmounted, watchEffect } from 'vue'
import { storeToRefs } from 'pinia'

import { CategoryFull } from '~/js/interfaces/facets'
import { getClasses } from '~/js/composables/classes'
import { getGlobalTranslation } from '~/js/composables/translations'
import { useTeambuildingState } from '~/js/stores/teambuildings-tm'

import TextProse from '~/vue/_teammasters/atoms/texts/TextProse.vue'
import IconFa from '~/vue/_teammasters/atoms/icons/IconFa.vue'

interface Props {
    disabled: boolean|null,
    dropdownPosition: 'left' | 'right'
    label: string
    multiSelect?: boolean
    options: Array<CategoryFull|null>,
    type: string|null,
    utilities?: string,
}

/** 
 * VARIABLES
 * **/
// props & emits
const props = withDefaults(defineProps<Props>(),{
    dropdownPosition: 'left',
    multiSelect: true, 
    options: () => [],
    type: null,
    utilities: '',
})
const emit = defineEmits(['input', 'active-dropdown'])

// store
const teambuildingStore = useTeambuildingState()
const { filters } = storeToRefs(teambuildingStore)

// variables
const isOpen = ref<boolean>(false)
const elDropdown = ref<HTMLElement | null>(null);
const elOptions = ref<HTMLElement[] | null>(null);
const elToggle = ref<HTMLElement | null>(null);
const activeIndex = ref<number>(-1)
const selected = ref<Array<string|null>|null>([])

const id = Math.floor(Math.random() * 10000000)
const classes = {
    wrapper: {
        _default: 'relative',
        color: 'bg-white',
        rounded: 'rounded-sm',
        focus: 'focus-within:ring-3 focus-within:ring-yellow',
        spacing: 'px-4 py-1 space-y-1',
    },
    toggle: {
        _default: 'relative z-10',
        color: 'text-neutral-700',
        display: 'flex justify-between items-center',
        font: 'font-primary text-base ',
        size: 'w-full',
        spacing: 'py-1',
    },
    list: {
        position: `absolute ${props.dropdownPosition}-0 top-full`,
        shadow: 'shadow-teammasters',
        rounded: 'rounded-b-xl',
        color: 'bg-white',
        size: 'w-full md:w-[calc(200%+8px)]',
        spacing: 'mt-2 gap-4 p-4 lg:p-6',
        display: 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3'
    },
    listItem: {
        _default: 'cursor-pointer',
        flex: 'flex items-center',
        spacing: 'px-2 py-1 xl:py-2 gap-2',
        border: 'border border-transparent',
        rounded: 'rounded-lg',
        focusVisible: 'focus-visible:ring-2 focus-visible:ring-offset-white dark:ring-offset-black focus-visible:ring-offset-2 focus-visible:outline-none focus-visible:ring-indigo',
        focus: 'focus:ring-2 focus:ring-offset-white dark:ring-offset-black focus:ring-offset-2 focus:outline-none focus:ring-indigo'
    },
    inputRadio: {
        _default: 'relative',
        radius: props.multiSelect ? 'rounded-sm' : 'rounded-full',
        border: 'border border-black',
        size: 'w-4 lg:w-6 h-4 lg:h-6',
    },
    inputRadioSelected: {
        _default: props.multiSelect ? 'after:rounded-xs lg:after:rounded-sm' : 'after:rounded-full',
        position: 'after:absolute after:left-1/2 after:top-1/2',
        transform: 'after:-translate-x-1/2 after:-translate-y-1/2',
        color: 'after:bg-black',
        size: 'after:w-2.5 after:lg:w-4.5 after:h-2.5 after:lg:h-4.5',
    },
}

/** 
 * METHODS
 * **/
const onToggleDropdown = async () => {
    isOpen.value = !isOpen.value

    if (isOpen.value) {
        await nextTick()
        activeIndex.value = 0
        elOptions.value?.[0]?.focus()
        emit('active-dropdown',props.type)
    } else {
        emit('active-dropdown',null)
    }
}

const onSelect = (option:CategoryFull|null) => {
    if (!option || !Array.isArray(selected.value)) {
        return
    }
    
    if (selected.value.includes(option.id)) {
        selected.value.splice(selected.value.indexOf(option.id), 1)
    } else {
        selected.value.push(option.id)
    }

    emit('input', {type: props.type, values: selected.value})
}

const handleClickOutside = (event) => {
    if (elDropdown.value && !elDropdown.value.contains(event.target)) {
        if (isOpen.value) {
            emit('active-dropdown',null)
        }
        isOpen.value = false
    }
};

const onHandleKeystroke = event => {
    if (!isOpen.value) return

    switch (event.key) {
            case 'Escape': {
                isOpen.value = false
                elToggle.value?.focus()
                break
            }
            case 'ArrowRight':
            case 'ArrowDown': {
                event.preventDefault()
                activeIndex.value = (activeIndex.value + 1) % props.options.length
                elOptions.value?.[activeIndex.value]?.focus()
                break
            }
            case 'ArrowLeft':
            case 'ArrowUp': {
                event.preventDefault()
                activeIndex.value = (activeIndex.value - 1 + props.options.length) % props.options.length
                elOptions.value?.[activeIndex.value]?.focus()
                break
            }
            case 'Enter': {
                event.preventDefault()

                if (!isOpen.value) {
                    isOpen.value = true
                    return
                }

                const active = elOptions.value?.[activeIndex.value] as HTMLElement | null;
                if (active) {
                    onSelect(JSON.parse(active.getAttribute('data-option') || '{}'))
                }

                break
            }
            case 'Tab': {
                event.preventDefault(); // Prevent default tabbing to move focus
                if (event.shiftKey) {
                    // Move focus to the previous item (Shift+Tab)
                    activeIndex.value = (activeIndex.value - 1 + props.options.length) % props.options.length;
                } else {
                    // Move focus to the next item (Tab)
                    activeIndex.value = (activeIndex.value + 1) % props.options.length;
                }
                elOptions.value?.[activeIndex.value]?.focus()
                break
            }
    }
}

const isChecked = (option: CategoryFull | null):boolean => {
    if (!option) {
        return false
    }

    if (selected.value) {
        return selected.value.indexOf(option.id) > -1
    }

    return false
}

const setCurrent = () => {
    if (!props.type) {
        return 
    }

    const current = filters?.value[props.type]

    if (!current) {
        return
    }

    selected.value = current?.values
}

/**
 * WACHTERS
 **/
watchEffect(() => {
    setCurrent()
})

onMounted(() => document.addEventListener('click', handleClickOutside));
onUnmounted(() => document.removeEventListener('click', handleClickOutside));
</script>

<template>
    <div 
        :class="[
            getClasses(classes, 'wrapper'),
            utilities,
            isOpen ? 'ring-3 ring-yellow z-20' : '',
            disabled ? 'after:bg-neutral-900/20 after:inset-0 after:w-full after:h-full after:absolute after:z-10' : '',
        ]" 
        ref="elDropdown"
    >
        <legend class="sr-only">{{label}}</legend>
        <label>
            <TextProse size="text-sm" utilities="flex items-center gap-1 pt-1">
                <span>{{ label }}</span>
                <span v-if="(selected?.length ?? 0) > 0" class="text-xs">({{ selected?.length ?? 0 }})</span>
            </TextProse>

            <button
                :aria-controls="`dropdown-menu-${id}`"
                :aria-expanded="isOpen"
                :class="getClasses(classes, 'toggle')"
                @click="onToggleDropdown"
                @keydown="onHandleKeystroke"
                aria-haspopup="listbox"
                ref="elToggle"
                type="button"
            >
                <span>{{ getGlobalTranslation('Select an option')}}</span>
                <IconFa icon="fas fa-chevron-down" size="text-xs"/>
            </button>

            <Transition
                enter-active-class="duration-500 ease-out"
                enter-from-class="opacity"
                enter-to-class="opacity-100"
                leave-active-class="duration-100 ease-in"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <ul
                    v-show="isOpen"
                    :class="getClasses(classes, 'list')"
                    :id="`dropdown-menu-${id}`"
                    role="listbox"
                    tabindex="-1"
                >
                    <template 
                        v-for="option in options"
                        :key="option?.id"
                    >
                        <li
                            v-if="option"
                            :class="getClasses(classes, 'listItem')"
                            :data-option="JSON.stringify(option)"
                            :key="option.id"
                            @click.prevent="onSelect(option)"
                            @keydown="onHandleKeystroke"
                            ref="elOptions"
                            role="option"
                            tabindex="0"
                        >
                            <span 
                                :class="[
                                    getClasses(classes, 'inputRadio'), 
                                    isChecked(option) ? getClasses(classes, 'inputRadioSelected') : null
                                ]" 
                            />

                            <TextProse color="text-neutral-700 hover:text-black">{{ option?.title }}</TextProse>
                        </li>
                    </template>
                </ul>
            </Transition>
        </label>
    </div>
</template>